import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    VStack,
    HStack,
    Heading,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Card,
    CardBody,
    CardHeader,
    Badge,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Spinner,
    Alert,
    AlertIcon,
    useColorModeValue,
    Checkbox,
    Flex,
    Text,
    Tooltip,
    Switch,
    Tag,
    TagLabel,
    TagCloseButton,
    Wrap,
    WrapItem,
    SimpleGrid,
    useToast
} from '@chakra-ui/react';
import {
    MdSearch,
    MdAdd,
    MdEdit,
    MdDelete,
    MdMoreVert,
    MdRefresh,
    MdUnlink,
    MdVerifiedUser,
    MdError,
    MdDownload
} from 'react-icons/md';
import ReactSelect from 'react-select';
import * as XLSX from 'xlsx';
import apiService from '../services/apiService';
import { formatDateTime, formatNumber } from '../utils/helpers';
import { ToastManager } from '../components/Common/ToastManager';

/**
 * Report Mapping Management Page Component
 */
function MapReport({ userProfile, appConfig }) {
    // State management for mappings data and UI state
    const [mappings, setMappings] = useState([]);
    const [filteredMappings, setFilteredMappings] = useState([]);
    const [clients, setClients] = useState([]);
    const [reports, setReports] = useState([]);
    const [availableClients, setAvailableClients] = useState([]); // Unmapped clients for selected report
    const [loadingClients, setLoadingClients] = useState(false);
    const [loading, setLoading] = useState(true);
    const [exportLoading, setExportLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [clientFilter, setClientFilter] = useState('all');
    const [reportFilter, setReportFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [verificationFilter, setVerificationFilter] = useState('all');
    const [cronTriggerFilter, setCronTriggerFilter] = useState('all');
    const [selectedMappings, setSelectedMappings] = useState([]);
    const [currentMapping, setCurrentMapping] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [reportFilterData,setReportFilterData]= useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        size: 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false
    });
    const[selectedModule, setSelectedModule]=useState('');
    
    // Modal states using Chakra UI disclosure hooks
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const { isOpen: isVerificationOpen, onOpen: onVerificationOpen, onClose: onVerificationClose } = useDisclosure();

    // Form state matching ReportMappingRequest structure from backend API
    const [formData, setFormData] = useState({
        reportCode: null,
        clientId: null,
        clientIds: [],
        isEnabled: true,
        createdBy: userProfile?.email || 'admin@emaildashboard.com',
        updatedBy: userProfile?.email || 'admin@emaildashboard.com',
        reportModule: null
    });

    // Verification form state for DCN validation status updates
    const [verificationData, setVerificationData] = useState({
        clientId: null,
        reportCode: null,
        isVerified: false,
        hasError: false
    });

    const [reportModules, setReportModules]= useState([
        { value: 'bulkmailv1', name: 'Bulk Mail V1' },
        { value: 'bulkmailv2', name: 'Bulk Mail V2' },
        { value: 'bulkmailv1v2', name: 'Bulk Mailer V1 and V2'},
        { value: 'others', name: 'Others' },
        { value: 'all', name: 'All'}
    ]);

    // Toast notification system for user feedback
    const toast = useToast();

    // Theme colors - all useColorModeValue hooks called at component top level to satisfy Rules of Hooks
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const headingColor = useColorModeValue('gray.800', 'white');
    const selectControlBg = useColorModeValue('white', 'gray.800');
    const selectMenuBg = useColorModeValue('white', 'gray.800');
    const selectOptionHoverBg = useColorModeValue('#f7fafc', '#2d3748');
    const selectMultiValueBg = useColorModeValue('#e2e8f0', '#4a5568');
    const selectMultiValueLabelColor = useColorModeValue('#2d3748', '#f7fafc');
    const activeFilterBg = useColorModeValue('blue.50', 'blue.900');
    const activeFilterBorderColor = useColorModeValue('blue.200', 'blue.700');
    const bulkActionBg = useColorModeValue('red.50', 'red.900');
    const bulkActionBorderColor = useColorModeValue('red.200', 'red.700');
    const readOnlyInputBg = useColorModeValue('gray.50', 'gray.700');

    /**
     * Load all mappings data for Excel export
     * Fetches complete dataset across all pages with current search/filter criteria
     */
    const loadAllMappingsForExport = async () => {
        try {
            // Build request parameters to get all data
            const requestParams = {
                page: 1,
                size: pagination.total > 0 ? pagination.total : 10000, // Use total count or large number
                sortBy: 'created_at_utc',
                sortDirection: 'DESC'
            };

            // Add search if present to maintain filter consistency
            if (searchTerm && searchTerm.trim()) {
                requestParams.search = searchTerm.trim();
            }

            // Add server-side filters when specific values are selected
            if (clientFilter !== 'all') {
                requestParams.clientId = clientFilter;
            }
            if (reportFilter !== 'all') {
                requestParams.reportCode = reportFilter;
            }
            if (statusFilter !== 'all') {
                requestParams.isEnabled = statusFilter === 'active';
            }
            if (verificationFilter !== 'all') {
                switch (verificationFilter) {
                    case 'verified':
                        requestParams.isVerified = true;
                        break;
                    case 'unverified':
                        requestParams.isVerified = false;
                        break;
                    case 'error':
                        requestParams.isVerificationError = true;
                        break;
                    default:
                        break;
                }
            }
            if (cronTriggerFilter !== 'all') {
                requestParams.isCronTriggered = cronTriggerFilter === 'enabled';
            }

            const response = await apiService.getReportMappingsList(requestParams);
            if (response.success && response.data) {
                return response.data.content || response.data || [];
            }
            throw new Error(response.message || 'Failed to load all mappings');
        } catch (error) {
            throw new Error(`Failed to fetch complete mapping data: ${error.message}`);
        }
    };

    /**
     * Export ALL mappings to Excel across all pages
     * Downloads complete mapping data in Excel format with proper formatting
     * Optimized to fetch all data before export regardless of current pagination
     */
    const handleExportMappingsExcel = async () => {
        try {
            setExportLoading(true);

            // Step 1: Fetch all mapping data across all pages
            const allMappingsData = await loadAllMappingsForExport();

            if (!allMappingsData || allMappingsData.length === 0) {
                ToastManager.showError('No mapping data available for export');
                return;
            }

            // Step 2: Prepare data for Excel export with complete dataset
            const excelData = allMappingsData.map(mapping => ({
                'Client ID': mapping.clientId,
                'Client Name': mapping.clientName,
                'Report Code': mapping.reportCode,
                'Report Description': mapping.reportDescription,
                'Status': mapping.isEnabled ? 'Active' : 'Disabled',
                'Verification Status': mapping.isVerified ? 'Verified' : 'Unverified',
                'Has Verification Error': mapping.isVerificationError ? 'Yes' : 'No',
                'Cron Trigger': mapping.isCronTriggered ? 'Enabled' : 'Disabled',
                'Created Date': formatDateTime(mapping.createdAtUtc),
                'Created By': mapping.createdBy || 'N/A',
                'Updated Date': formatDateTime(mapping.updatedAtUtc),
                'Updated By': mapping.updatedBy || 'N/A'
            }));

            // Step 3: Create workbook and worksheet
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet(excelData);

            // Step 4: Set column widths for better readability
            const columnWidths = [
                { wch: 12 }, // Client ID
                { wch: 25 }, // Client Name
                { wch: 15 }, // Report Code
                { wch: 40 }, // Report Description
                { wch: 12 }, // Status
                { wch: 18 }, // Verification Status
                { wch: 20 }, // Has Verification Error
                { wch: 15 }, // Cron Trigger
                { wch: 20 }, // Created Date
                { wch: 25 }, // Created By
                { wch: 20 }, // Updated Date
                { wch: 25 }  // Updated By
            ];
            worksheet['!cols'] = columnWidths;

            // Step 5: Add worksheet to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, 'All Mappings');

            // Step 6: Add comprehensive metadata worksheet
            const metadataSheet = XLSX.utils.json_to_sheet([
                { Property: 'Export Date', Value: new Date().toISOString() },
                { Property: 'Total Records Exported', Value: allMappingsData.length },
                { Property: 'Current Page Records', Value: filteredMappings.length },
                { Property: 'Total Database Records', Value: pagination.total },
                { Property: 'Search Term Applied', Value: searchTerm || 'None' },
                { Property: 'Client Filter Applied', Value: clientFilter === 'all' ? 'All Clients' : clientFilter },
                { Property: 'Report Filter Applied', Value: reportFilter === 'all' ? 'All Reports' : reportFilter },
                { Property: 'Status Filter Applied', Value: statusFilter === 'all' ? 'All Statuses' : statusFilter },
                { Property: 'Verification Filter Applied', Value: verificationFilter === 'all' ? 'All Verification' : verificationFilter },
                { Property: 'Cron Filter Applied', Value: cronTriggerFilter === 'all' ? 'All Cron' : cronTriggerFilter },
                { Property: 'Export Scope', Value: 'Complete Dataset (All Pages)' },
                { Property: 'Exported By', Value: userProfile?.email || 'System Administrator' },
                { Property: 'Application', Value: 'Report Mapping Management System' },
                { Property: 'System Version', Value: '1.0.0' }
            ]);
            metadataSheet['!cols'] = [{ wch: 25 }, { wch: 35 }];
            XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Export Metadata');

            // Step 7: Add summary statistics worksheet
            const statusCounts = allMappingsData.reduce((acc, mapping) => {
                const status = mapping.isEnabled ? 'Active' : 'Disabled';
                acc[status] = (acc[status] || 0) + 1;
                return acc;
            }, {});

            const verificationCounts = allMappingsData.reduce((acc, mapping) => {
                const verification = mapping.isVerified ? 'Verified' : 'Unverified';
                acc[verification] = (acc[verification] || 0) + 1;
                return acc;
            }, {});

            const cronCounts = allMappingsData.reduce((acc, mapping) => {
                const cron = mapping.isCronTriggered ? 'Enabled' : 'Disabled';
                acc[cron] = (acc[cron] || 0) + 1;
                return acc;
            }, {});

            const summaryData = [
                { Metric: 'Total Mappings', Value: allMappingsData.length },
                { Metric: 'Active Mappings', Value: statusCounts['Active'] || 0 },
                { Metric: 'Disabled Mappings', Value: statusCounts['Disabled'] || 0 },
                { Metric: 'Verified Mappings', Value: verificationCounts['Verified'] || 0 },
                { Metric: 'Unverified Mappings', Value: verificationCounts['Unverified'] || 0 },
                { Metric: 'Verification Errors', Value: allMappingsData.filter(m => m.isVerificationError).length },
                { Metric: 'Cron Enabled', Value: cronCounts['Enabled'] || 0 },
                { Metric: 'Cron Disabled', Value: cronCounts['Disabled'] || 0 },
                { Metric: 'Unique Clients', Value: new Set(allMappingsData.map(m => m.clientId)).size },
                { Metric: 'Unique Reports', Value: new Set(allMappingsData.map(m => m.reportCode)).size }
            ];

            const summarySheet = XLSX.utils.json_to_sheet(summaryData);
            summarySheet['!cols'] = [{ wch: 25 }, { wch: 15 }];
            XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary Statistics');

            // Step 8: Generate filename with current date and scope indicator
            const filename = `mappings_complete_export_${new Date().toISOString().split('T')[0]}.xlsx`;

            // Step 9: Write and download the file
            XLSX.writeFile(workbook, filename);

            ToastManager.showSuccess(
                `Complete mapping database exported successfully! 
                ${allMappingsData.length} records exported to ${filename}`
            );

        } catch (error) {
            ToastManager.showError(`Failed to export complete mapping data: ${error.message}`);
        } finally {
            setExportLoading(false);
        }
    };

    /**
     * Initialize component and load data following CAS backend pagination structure
     * Sets up initial state and data fetching with comprehensive error handling
     * Called once on component mount to establish baseline data
     */
    const loadInitialData = useCallback(async () => {
        try {
            setLoading(true);
            
            // Parallel data fetching for optimal performance using Promise.all
            // Fetches mappings, clients, reports, and statistics simultaneously
            const [mappingsResponse, clientsResponse, reportsResponse, statsResponse] = await Promise.all([
                apiService.getReportMappingsList({
                    page: 1,
                    size: pagination.size,
                    search: searchTerm
                }),
                apiService.getActiveClients(),
                apiService.getReports({ size:1000}),
                apiService.getReportMappingStatistics()
            ]);

            // Process mappings response with proper pagination handling
            // Supports both paginated and non-paginated response structures
            if (mappingsResponse.success && mappingsResponse.data) {
                const mappingsData = mappingsResponse.data.content || mappingsResponse.data;
                setMappings(mappingsData);
                
                // Update pagination metadata if provided by backend
                if (mappingsResponse.data.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        total: mappingsResponse.data.pagination.total,
                        totalPages: mappingsResponse.data.pagination.totalPages,
                        hasNext: mappingsResponse.data.pagination.hasNext || false,
                        hasPrevious: mappingsResponse.data.pagination.hasPrevious || false
                    }));
                }
            }

            setReportModules(reportModules)

            // Process clients response with fallback for different response structures
            // Handles both direct array and nested object responses
            if (clientsResponse.success) {
                const clientsData = Array.isArray(clientsResponse.data) 
                    ? clientsResponse.data 
                    : clientsResponse.data.clients || [];
                setClients(clientsData);
            }

            
            // Process reports response supporting paginated and non-paginated formats
            // Extracts report data from various response structures
            if (reportsResponse.success && selectedModule !=='') {
                const reportsData = Array.isArray(reportsResponse.data) 
                    ? reportsResponse.data 
                    : reportsResponse.data.content || [];
                setReports(reportsData);
            }else{
                const reportsData = Array.isArray(reportsResponse.data) 
                    ? reportsResponse.data 
                    : reportsResponse.data.content || [];
                setReportFilterData(reportsData);
            }
            // Process statistics response for dashboard metrics display
            // Provides mapping performance and verification metrics
            if (statsResponse.success && statsResponse.data) {
                setStatistics(statsResponse.data);
            }

        } catch (error) {
            ToastManager.showError('Failed to load mapping data');
        } finally {
            setLoading(false);
        }
    }, [pagination.size, searchTerm]);

    /**
     * Load specific page of mappings with server-side filtering and pagination
     * Implements comprehensive filtering and sorting for optimal data retrieval
     * Supports search, status, verification, and cron trigger filtering
     */
    const loadMappingsPage = useCallback(async () => {
        try {
            setLoading(true);
            
            // Build comprehensive filter parameters for server-side processing
            // Includes pagination, search, and all available filter options
            const filterParams = {
                page: pagination.page,
                size: pagination.size,
                search: searchTerm,
                sortBy: 'created_at_utc',
                sortDirection: 'DESC'
            };

            // Add server-side filters when specific values are selected
            // Filters are only applied when not set to 'all' to avoid unnecessary constraints
            if (clientFilter !== 'all') {
                filterParams.clientId = clientFilter;
            }
            if (reportFilter !== 'all') {
                filterParams.reportCode = reportFilter;
            }
            if (statusFilter !== 'all') {
                filterParams.isEnabled = statusFilter === 'active';
            }
            if (verificationFilter !== 'all') {
                // Handle multiple verification states with appropriate boolean mapping
                switch (verificationFilter) {
                    case 'verified':
                        filterParams.isVerified = true;
                        break;
                    case 'unverified':
                        filterParams.isVerified = false;
                        break;
                    case 'error':
                        filterParams.isVerificationError = true;
                        break;
                    default:
                        // No additional filtering for 'all' case
                        break;
                }
            }
            if (cronTriggerFilter !== 'all') {
                filterParams.isCronTriggered = cronTriggerFilter === 'enabled';
            }

            // Execute filtered request to backend API
            const response = await apiService.getReportMappingsList(filterParams);

            if (response.success && response.data) {
                const mappingsData = response.data.content || response.data;
                setMappings(mappingsData);
                
                // Update pagination state with server response
                if (response.data.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        total: response.data.pagination.total,
                        totalPages: response.data.pagination.totalPages,
                        hasNext: response.data.pagination.hasNext || false,
                        hasPrevious: response.data.pagination.hasPrevious || false
                    }));
                }
            }

        } catch (error) {
            ToastManager.showError('Failed to load mappings');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.size, searchTerm, clientFilter, reportFilter, statusFilter, verificationFilter, cronTriggerFilter]);

    /**
     * Fetch unmapped clients for selected report in create mapping form
     * Filters out clients that already have mappings for the specified report
     * Updates availableClients state with only unmapped clients
     */
    const fetchUnmappedClients = useCallback(async (reportCode) => {
        try {
            setLoadingClients(true);
            
            if (!reportCode) {
                // If no report selected, show all active clients
                setAvailableClients(clients);
                return;
            }

            // Fetch all mappings for the selected report
            const [mappingsResponse] = await Promise.all([
                apiService.getReportMappingsByReport(reportCode)
            ]);

            if (mappingsResponse.success && mappingsResponse.data) {
                // Extract client IDs that are already mapped to this report
                const mappedClientIds = new Set();
                const reportMappings = mappingsResponse.data.content || mappingsResponse.data || [];
                
                reportMappings.forEach(mapping => {
                    if (mapping.clientId) {
                        mappedClientIds.add(mapping.clientId);
                    }
                });

                // Filter out already mapped clients from available clients list
                const unmappedClients = clients.filter(client => 
                    !mappedClientIds.has(client.clientId)
                );

                setAvailableClients(unmappedClients);
            } else {
                // If no mappings found or error, all clients are available
                setAvailableClients(clients);
            }

        } catch (error) {
            // On error, show all clients to prevent blocking user
            setAvailableClients(clients);
            ToastManager.showError('Failed to load available clients');
        } finally {
            setLoadingClients(false);
        }
    }, [clients]);




    /**
     * Filter mappings based on search term and applied filters
     * Implements client-side filtering for enhanced user experience
     * Provides real-time filtering without server round-trips
     */
    const filterMappings = useCallback(() => {
        let filtered = [...mappings];

        // Apply text search across client name, report description, and report code
        // Case-insensitive search for better user experience
        if (searchTerm.trim()) {
            filtered = filtered.filter(mapping =>
                mapping.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                mapping.reportDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                mapping.reportCode?.toString().includes(searchTerm)
            );
        }

        // Apply client-specific filtering when not set to show all clients
        if (clientFilter !== 'all') {
            filtered = filtered.filter(mapping => 
                mapping.clientId?.toString() === clientFilter
            );
        }

        // Apply report-specific filtering when specific report is selected
        if (reportFilter !== 'all') {
            filtered = filtered.filter(mapping => 
                mapping.reportCode?.toString() === reportFilter
            );
        }

        // Apply status filtering based on enabled/disabled state
        if (statusFilter !== 'all') {
            filtered = filtered.filter(mapping => {
                switch (statusFilter) {
                    case 'active':
                        return mapping.isEnabled;
                    case 'disabled':
                        return !mapping.isEnabled;
                    default:
                        return true;
                }
            });
        }

        // Apply verification status filtering with multiple verification states
        if (verificationFilter !== 'all') {
            filtered = filtered.filter(mapping => {
                switch (verificationFilter) {
                    case 'verified':
                        return mapping.isVerified && !mapping.isVerificationError;
                    case 'unverified':
                        return !mapping.isVerified;
                    case 'error':
                        return mapping.isVerificationError;
                    default:
                        return true;
                }
            });
        }

        // Apply cron trigger filtering based on automation status
        if (cronTriggerFilter !== 'all') {
            filtered = filtered.filter(mapping => {
                switch (cronTriggerFilter) {
                    case 'enabled':
                        return mapping.isCronTriggered;
                    case 'disabled':
                        return !mapping.isCronTriggered;
                    default:
                        return true;
                }
            });
        }

        setFilteredMappings(filtered);
    }, [mappings, searchTerm, clientFilter, reportFilter, statusFilter, verificationFilter, cronTriggerFilter]);

    /**
     * Generate client options for React Select components in create mapping
     * Shows only clients that are NOT already mapped to the selected report
     * Returns array of option objects with value-label pairs for unmapped clients only
     */
    const getAvailableClientOptions = useCallback(() => {
        return availableClients.map(client => ({
            value: client.clientId,
            label: `${client.clientId} - ${client.clientName}`
        }));
    }, [availableClients]);

    /**
     * Generate client options for React Select components (for filtering)
     * Shows all clients for filtering purposes in the main table
     * Returns array of option objects with value-label pairs
     */
    const getClientOptions = useCallback(() => {
        return clients.map(client => ({
            value: client.clientId,
            label: `${client.clientId} - ${client.clientName}`
        }));
    }, [clients]);

    /**
     * Generate report options for React Select components  
     * Formats report data for dropdown selection with code and description
     * Returns array of option objects for report selection
     */
    const getReportOptions = useCallback(() => {
        return reports.map(report => ({
            value: report.reportCode,
            label: `${report.reportCode} - ${report.reportDescription}`
        }));
    }, [reports]);

    const getReportModules = useCallback(() =>{
        return reportModules.map(module =>({
            value: module.value,
            label: `${module.name}`
        }))
    }, [reportModules])

    /**
     * Handle client selection change in create mapping form
     * Manages "all clients" selection logic and multi-selection validation
     * Uses only unmapped clients for the selected report to prevent duplicates
     */
    const handleClientSelectionChange = useCallback((selectedOptions) => {
        if (!selectedOptions) {
            setFormData(prev => ({ ...prev, clientIds: [] }));
            return;
        }

        const selectedValues = selectedOptions.map(option => option.value);
        
        // Handle "all clients" selection with exclusive logic
        // When "all" is selected, use all available (unmapped) clients for this report
        if (selectedValues.includes('all')) {
            // User selected "all" - map to all available unmapped clients
            if (!formData.clientIds.includes('all')) {
                setFormData(prev => ({ ...prev, clientIds: ['all'] }));
            }
        } else {
            // Normal multi-selection without "all" option
            setFormData(prev => ({ ...prev, clientIds: selectedValues }));
        }
    }, [formData.clientIds]);

    /**
     * Handle report selection change in create mapping form
     * Fetches unmapped clients for the selected report and resets client selection
     */
    const handleReportSelectionChange = useCallback((selectedOption) => {
        const reportCode = selectedOption?.value || null;
        
        // Update form data with selected report
        setFormData(prev => ({ 
            ...prev, 
            reportCode: reportCode,
            clientIds: [] // Reset client selection when report changes
        }));

        // Fetch available (unmapped) clients for the selected report
        if (reportCode) {
            fetchUnmappedClients(reportCode);
        } else {
            // If no report selected, show all clients
            setAvailableClients(clients);
        }
    }, [fetchUnmappedClients, clients]);

    const handleReportModuleChange = useCallback((selectedOption) => {
        const module = selectedOption?.label;
        const value = selectedOption?.value;
        if (value) {
            setFormData(prev => ({
                ...prev,
                reportModule: value,  // Update the module
                reportCode: null      // Reset the report (use null to match your initial state)
            }));
            setSelectedModule(value);
            setModuleBasedReports(value);
        } else {
            setFormData(prev => ({
                ...prev,
                reportModule: null,   // Reset module to null
                reportCode: null      // Reset report to null
            }));
            setSelectedModule('');
        }
    },[])

    const setModuleBasedReports = useCallback(async (module) => {
        try {
            
            if (!module) {
                return;
            }
            const[reportResponse] = await Promise.all([
                
                apiService.getReportByModule(module)
            ])
            if (reportResponse.success) {
                const reportsData = Array.isArray(reportResponse.data) 
                    ? reportResponse.data 
                    : reportResponse.data.content || [];
                setReports(reportsData);
            }
        } catch (error) {
            // On error, show all clients to prevent blocking user
            // setAvailableClients(clients);
            ToastManager.showError('Failed to load available clients');
        } finally {
            // setLoadingClients(false);
        }
    }, [reports]);


    /**
     * Get selected client options for React Select display in create mapping
     * Handles "all clients" display properly using only available unmapped clients
     * Returns formatted options for controlled React Select component
     */
    const getSelectedClientOptions = useCallback(() => {
        if (formData.clientIds.includes('all')) {
            return [{ value: 'all', label: `All Available Clients (${availableClients.length})` }];
        }
        return getAvailableClientOptions().filter(opt => 
            formData.clientIds.includes(opt.value)
        );
    }, [formData.clientIds, getAvailableClientOptions, availableClients.length]);

    /**
     * Handle mapping creation following ReportMappingRequest structure
     * Creates new report-client mappings with comprehensive validation
     * Supports both single and batch creation using only unmapped clients
     */
    const handleCreateMapping = useCallback(async () => {
        try {
            // Validate required fields before proceeding with API call
            if (!formData.reportCode) {
                ToastManager.showError('Please select a report');
                return;
            }

            if (formData.clientIds.length === 0) {
                ToastManager.showError('Please select at least one client');
                return;
            }

            // Check if no unmapped clients are available for selected report
            if (availableClients.length === 0) {
                ToastManager.showError('No unmapped clients available for this report');
                return;
            }

            // Determine if mapping for all available clients is requested
            const isAllClients = formData.clientIds.some(id => id === 'all');
            
            // Prepare client IDs - use only available (unmapped) clients to prevent duplicates
            let clientIdsToMap = formData.clientIds;
            if (isAllClients) {
                clientIdsToMap = availableClients.map(client => client.clientId);
            }
            
            // Final validation - ensure we're not trying to map to already mapped clients
            const validClientIds = clientIdsToMap.filter(clientId => 
                clientId !== 'all' && availableClients.some(client => client.clientId === clientId)
            );

            if (validClientIds.length === 0) {
                ToastManager.showError('No valid unmapped clients selected');
                return;
            }
            
            // Prepare request data following backend ReportMappingRequest structure
            const mappingData = {
                reportCode: formData.reportCode,
                clientIds: validClientIds,
                isEnabled: formData.isEnabled,
                createdBy: formData.createdBy
            };

            let response;
            
            // Use appropriate endpoint based on number of clients being mapped
            // Batch endpoint for multiple clients, single endpoint for individual mapping
            if (mappingData.clientIds.length > 1) {
                response = await apiService.createBatchReportMappings(mappingData);
            } else {
                // Transform to single mapping request format for individual client
                const singleMappingData = {
                    reportCode: mappingData.reportCode,
                    clientId: mappingData.clientIds[0],
                    isEnabled: mappingData.isEnabled,
                    createdBy: mappingData.createdBy
                };
                response = await apiService.createReportMapping(singleMappingData);
            }
            
            // Handle successful creation with user feedback
            if (response.success) {
                const clientCount = mappingData.clientIds.length;
                ToastManager.showSuccess(`${clientCount} mapping(s) created successfully`);
                onCreateClose();
                resetForm();
                loadInitialData();
            } else {
                ToastManager.showError(response.message || 'Failed to create mapping');
            }
        } catch (error) {
            ToastManager.showError('Failed to create mapping');
        }
    }, [formData, availableClients, onCreateClose, loadInitialData]);

    /**
     * Handle mapping update following backend update structure
     * Updates existing mapping with field-level validation and error handling
     * Supports status changes and metadata updates
     */
    const handleUpdateMapping = useCallback(async () => {
        try {
            if (!currentMapping) return;

            // Prepare update data following backend ReportMappingRequest structure
            const mappingData = {
                isEnabled: formData.isEnabled,
                updatedBy: formData.updatedBy
            };

            // Execute update using composite key (clientId + reportCode)
            const response = await apiService.updateReportMapping(
                currentMapping.clientId,
                currentMapping.reportCode,
                mappingData
            );
            
            if (response.success) {
                ToastManager.showSuccess('Mapping updated successfully');
                onEditClose();
                resetForm();
                loadInitialData();
            } else {
                ToastManager.showError(response.message || 'Failed to update mapping');
            }
        } catch (error) {
            ToastManager.showError('Failed to update mapping');
        }
    }, [currentMapping, formData.isEnabled, formData.updatedBy, onEditClose, loadInitialData]);

    /**
     * Handle mapping deletion with confirmation and error handling
     * Deletes individual mapping using composite key identification
     * Provides user feedback and refreshes data upon successful deletion
     */
    const handleDeleteMapping = useCallback(async () => {
        try {
            if (!currentMapping) return;

            // Execute deletion using composite key (clientId + reportCode)
            const response = await apiService.deleteReportMapping(
                currentMapping.clientId,
                currentMapping.reportCode
            );
            
            if (response.success) {
                ToastManager.showSuccess('Mapping deleted successfully');
                onDeleteClose();
                setCurrentMapping(null);
                loadInitialData();
            } else {
                ToastManager.showError(response.message || 'Failed to delete mapping');
            }
        } catch (error) {
            ToastManager.showError('Failed to delete mapping');
        }
    }, [currentMapping, onDeleteClose, loadInitialData]);

    /**
     * Handle bulk mapping deletion for selected mappings
     * Processes multiple deletions using batch delete endpoint
     * Provides comprehensive user feedback and selection cleanup
     */
    const handleBulkDelete = useCallback(async () => {
        try {
            if (selectedMappings.length === 0) return;

            // Transform selected mapping IDs to backend-expected format
            // Parses composite IDs back to clientId and reportCode pairs
            const mappings = selectedMappings.map(mappingId => {
                const [clientId, reportCode] = mappingId.split('_');
                return {
                    clientId: parseInt(clientId, 10),
                    reportCode: parseInt(reportCode, 10)
                };
            });

            // Execute batch deletion using backend batch endpoint
            const response = await apiService.batchDeleteReportMappings({ mappings });
            
            if (response.success) {
                ToastManager.showSuccess(`${selectedMappings.length} mappings deleted successfully`);
                setSelectedMappings([]);
                loadInitialData();
            } else {
                ToastManager.showError(response.message || 'Failed to delete selected mappings');
            }
        } catch (error) {
            ToastManager.showError('Failed to delete selected mappings');
        }
    }, [selectedMappings, loadInitialData]);

    /**
     * Handle verification status update for DCN validation
     * Updates verification flags using backend verification endpoint
     * Supports verified, unverified, and error states
     */
    const handleUpdateVerification = useCallback(async () => {
        try {
            // Execute verification status update with current form data
            const response = await apiService.updateVerificationStatus({
                clientId: verificationData.clientId,
                reportCode: verificationData.reportCode,
                isVerified: verificationData.isVerified,
                hasError: verificationData.hasError
            });

            if (response.success) {
                ToastManager.showSuccess('Verification status updated successfully');
                onVerificationClose();
                loadInitialData();
            } else {
                ToastManager.showError(response.message || 'Failed to update verification status');
            }
        } catch (error) {
            ToastManager.showError('Failed to update verification status');
        }
    }, [verificationData, onVerificationClose, loadInitialData]);

    /**
     * Handle cron trigger status toggle for individual mapping
     * Updates automation trigger status using backend cron endpoint
     * Provides immediate visual feedback and data refresh
     */
    const handleToggleCronTrigger = useCallback(async (mapping) => {
        try {
            // Execute cron trigger status toggle with inverted current state
            const response = await apiService.updateCronTriggerStatus({
                clientId: mapping.clientId,
                reportCode: mapping.reportCode,
                isTriggered: !mapping.isCronTriggered
            });

            if (response.success) {
                ToastManager.showSuccess('Cron trigger status updated successfully');
                loadInitialData();
            } else {
                ToastManager.showError(response.message || 'Failed to update cron trigger status');
            }
        } catch (error) {
            ToastManager.showError('Failed to update cron trigger status');
        }
    }, [loadInitialData]);

    /**
     * Open edit modal with mapping data pre-population
     * Prepares form state for editing existing mapping
     * Sets current mapping context and opens edit modal
     */
    const openEditModal = useCallback((mapping) => {
        setCurrentMapping(mapping);
        setFormData({
            reportCode: mapping.reportCode,
            clientId: mapping.clientId,
            clientIds: [mapping.clientId],
            isEnabled: mapping.isEnabled,
            updatedBy: userProfile?.email || 'admin@emaildashboard.com'
        });
        onEditOpen();
    }, [userProfile?.email, onEditOpen]);

    /**
     * Open delete confirmation modal
     * Sets current mapping context for deletion confirmation
     * Triggers delete confirmation modal display
     */
    const openDeleteModal = useCallback((mapping) => {
        setCurrentMapping(mapping);
        onDeleteOpen();
    }, [onDeleteOpen]);

    /**
     * Open verification modal with current verification data
     * Prepares verification form with existing mapping verification status
     * Enables DCN validation status management
     */
    const openVerificationModal = useCallback((mapping) => {
        setVerificationData({
            clientId: mapping.clientId,
            reportCode: mapping.reportCode,
            isVerified: mapping.isVerified,
            hasError: mapping.isVerificationError
        });
        onVerificationOpen();
    }, [onVerificationOpen]);

    /**
     * Reset form data to initial state
     * Clears all form fields, current mapping context, and available clients
     * Used after successful operations or modal cancellation
     */
    const resetForm = useCallback(() => {
        setFormData({
            reportCode: null,
            clientId: null,
            clientIds: [],
            isEnabled: true,
            createdBy: userProfile?.email || 'admin@emaildashboard.com',
            updatedBy: userProfile?.email || 'admin@emaildashboard.com'
        });
        setCurrentMapping(null);
        setAvailableClients(clients); // Reset to all clients when no report selected
    }, [userProfile?.email, clients]);

    /**
     * Handle mapping selection for bulk operations
     * Manages checkbox selection state for bulk action processing
     * Adds or removes mappings from selection array
     */
    const handleMappingSelection = useCallback((mappingId, isSelected) => {
        if (isSelected) {
            setSelectedMappings(prev => [...prev, mappingId]);
        } else {
            setSelectedMappings(prev => prev.filter(id => id !== mappingId));
        }
    }, []);

    /**
     * Handle select all mappings toggle functionality
     * Manages bulk selection of all visible mappings
     * Provides convenient selection management for bulk operations
     */
    const handleSelectAll = useCallback((isSelected) => {
        if (isSelected) {
            setSelectedMappings(filteredMappings.map(mapping => 
                `${mapping.clientId}_${mapping.reportCode}`
            ));
        } else {
            setSelectedMappings([]);
        }
    }, [filteredMappings]);

    /**
     * Reset all filters to default values
     * Clears all applied filters and search terms
     * Resets pagination to first page and triggers data reload
     */
    const resetFilters = useCallback(() => {
        setSearchTerm('');
        setClientFilter('all');
        setReportFilter('all');
        setStatusFilter('all');
        setVerificationFilter('all');
        setCronTriggerFilter('all');
        setPagination(prev => ({ ...prev, page: 1 }));
        // Data will reload automatically due to useEffect dependencies
    }, []);

    /**
     * Handle pagination change for server-side pagination
     * Updates current page state and triggers data reload
     * Provides navigation through large datasets
     */
    const handlePageChange = useCallback((newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    }, []);

    /**
     * Handle page size change
     * Updates size and resets to page 1
     */
    const handlePageSizeChange = useCallback((newSize) => {
        setPagination(prev => ({ 
            ...prev, 
            page: 1,  // Reset to first page
            size: parseInt(newSize, 10) 
        }));
    }, []);

    /**
     * Get mapping status badge properties based on current state
     * Returns appropriate color scheme and label for status visualization
     * Handles enabled, disabled, verified, and error states
     */
    const getStatusBadgeProps = useCallback((mapping) => {
        if (!mapping.isEnabled) {
            return { colorScheme: 'gray', label: 'Disabled' };
        }
        
        if (mapping.isVerificationError) {
            return { colorScheme: 'red', label: 'Verification Failed' };
        }
        
        if (!mapping.isVerified) {
            return { colorScheme: 'yellow', label: 'Pending Verification' };
        }
        
        return { colorScheme: 'green', label: 'Active' };
    }, []);

    /**
     * Get count of currently active filters
     * Returns number of filters currently applied for UI badge display
     * Excludes default 'all' values from count
     */
    const getActiveFiltersCount = useCallback(() => {
        let count = 0;
        if (searchTerm.trim()) count++;
        if (clientFilter !== 'all') count++;
        if (reportFilter !== 'all') count++;
        if (statusFilter !== 'all') count++;
        if (verificationFilter !== 'all') count++;
        if (cronTriggerFilter !== 'all') count++;
        return count;
    }, [searchTerm, clientFilter, reportFilter, statusFilter, verificationFilter, cronTriggerFilter]);

    /**
     * Check if any filters are currently active
     * Returns boolean indicating whether any filter is applied
     * Used for conditional UI element display
     */
    const hasActiveFilters = useCallback(() => {
        return getActiveFiltersCount() > 0;
    }, [getActiveFiltersCount]);

    /**
     * Custom React Select styles for theme compatibility
     * Ensures select components match application theme across light/dark modes
     * Provides consistent styling with Chakra UI design system
     */
    const selectStyles = useMemo(() => ({
        control: (provided) => ({
            ...provided,
            backgroundColor: selectControlBg,
            borderColor: borderColor,
            minHeight: '40px'
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: selectMenuBg,
            border: `1px solid ${borderColor}`
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected 
                ? '#4318ff'
                : state.isFocused 
                    ? selectOptionHoverBg
                    : 'transparent',
            color: state.isSelected ? 'white' : textColor
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: selectMultiValueBg
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: selectMultiValueLabelColor
        })
    }), [selectControlBg, borderColor, selectMenuBg, selectOptionHoverBg, textColor, selectMultiValueBg, selectMultiValueLabelColor]);

    /**
     * Initialize available clients when clients data is loaded
     * Sets initial state to show all clients when no report is selected
     */
    useEffect(() => {
        if (clients.length > 0) {
            setAvailableClients(clients);
        }
    }, [clients]);

    /**
     * Initialize component and load data on mount
     * Triggers initial data loading when component first renders
     */
    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);

    /**
     * Apply client-side filtering when data or filters change
     * Provides real-time filtering feedback without server requests
     */
    useEffect(() => {
        filterMappings();
    }, [filterMappings]);

    /**
     * Load data when pagination changes (Fixed pagination loading)
     * Handles page navigation for server-side pagination
     */
    useEffect(() => {
        loadMappingsPage();
    }, [pagination.page, pagination.size]);

    /**
     * Debounced filter change handler for server-side filtering
     * Implements 500ms debounce to prevent excessive API calls during typing
     * Resets to page 1 only when filters change, not when pagination changes
     */
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            // Only reset to page 1 and reload when filters change
            setPagination(prev => ({ ...prev, page: 1 }));
            // Need to call loadMappingsPage after page is reset
            const loadWithDelay = setTimeout(() => {
                loadMappingsPage();
            }, 50); // Small delay to ensure page state is updated
            
            return () => clearTimeout(loadWithDelay);
        }, 500); // 500ms debounce for optimal performance

        return () => clearTimeout(timeoutId);
    }, [searchTerm, clientFilter, reportFilter, statusFilter, verificationFilter, cronTriggerFilter]);

    return (
        <Box>
            <VStack spacing={6} align="stretch">
                {/* Page Header with title, description, and action buttons */}
                <Flex justify="space-between" align="center">
                    <Box>
                        <Heading size="lg" color={headingColor}>
                            Report Mapping
                        </Heading>
                        <Text color={textColor} mt={1}>
                            Map reports to clients for execution and manage verification status
                        </Text>
                    </Box>
                    <HStack spacing={3}>
                        <Button
                            leftIcon={<MdRefresh />}
                            onClick={loadInitialData}
                            variant="outline"
                            size="sm"
                        >
                            Refresh
                        </Button>
                        <Tooltip label="Export complete mapping database to Excel (.xlsx) - All pages included" hasArrow placement="bottom">
                            <Button
                                leftIcon={<MdDownload />}
                                onClick={handleExportMappingsExcel}
                                variant="outline"
                                size="sm"
                                isLoading={exportLoading}
                                loadingText="Exporting..."
                                isDisabled={pagination.total === 0}
                            >
                                Export All Data
                            </Button>
                        </Tooltip>
                        <Button
                            leftIcon={<MdAdd />}
                            onClick={onCreateOpen}
                            colorScheme="brand"
                            size="sm"
                        >
                            Create Mapping
                        </Button>
                    </HStack>
                </Flex>

                {/* Statistics Cards displaying key metrics */}
                {statistics && (
                    <HStack spacing={4} wrap="wrap">
                        <Card bg={cardBg} borderColor={borderColor} minW="200px">
                            <CardBody p={4}>
                                <VStack align="start" spacing={1}>
                                    <Text fontSize="sm" color={textColor}>Total Mappings</Text>
                                    <Text fontSize="2xl" fontWeight="bold" color={headingColor}>
                                        {formatNumber(statistics.totalMappings)}
                                    </Text>
                                </VStack>
                            </CardBody>
                        </Card>
                        <Card bg={cardBg} borderColor={borderColor} minW="200px">
                            <CardBody p={4}>
                                <VStack align="start" spacing={1}>
                                    <Text fontSize="sm" color={textColor}>Verified Mappings</Text>
                                    <Text fontSize="2xl" fontWeight="bold" color="green.500">
                                        {formatNumber(statistics.verifiedMappings)}
                                    </Text>
                                </VStack>
                            </CardBody>
                        </Card>
                        <Card bg={cardBg} borderColor={borderColor} minW="200px">
                            <CardBody p={4}>
                                <VStack align="start" spacing={1}>
                                    <Text fontSize="sm" color={textColor}>Success Rate</Text>
                                    <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                                        {statistics.verificationSuccessRate?.toFixed(1)}%
                                    </Text>
                                </VStack>
                            </CardBody>
                        </Card>
                    </HStack>
                )}

                {/* Comprehensive Filters and Search Section */}
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardBody>
                        <VStack spacing={4} align="stretch">
                            {/* Main Search Bar with reset filters functionality */}
                            <Flex 
                                direction={{ base: 'column', md: 'row' }} 
                                gap={4} 
                                align={{ base: 'stretch', md: 'center' }}
                                w="full"
                            >
                                <InputGroup flex="1" maxW={{ base: 'full', md: '400px' }}>
                                    <InputLeftElement>
                                        <MdSearch color={textColor} />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search by client name, report description, or report code..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </InputGroup>
                                
                                <Button
                                    size="md"
                                    onClick={resetFilters}
                                    variant="outline"
                                    isDisabled={!hasActiveFilters()}
                                    minW="140px"
                                    flexShrink={0}
                                >
                                    Reset Filters
                                    {hasActiveFilters() && (
                                        <Badge ml={2} colorScheme="blue" variant="solid" fontSize="xs">
                                            {getActiveFiltersCount()}
                                        </Badge>
                                    )}
                                </Button>
                            </Flex>

                            {/* Filter Grid with all available filter options */}
                            <Box>
                                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={3}>
                                    Filters
                                </Text>
                                <SimpleGrid 
                                    columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 5 }} 
                                    spacing={4}
                                    w="full"
                                >
                                    <Box>
                                        <FormLabel fontSize="sm" color={textColor} mb={1}>Report</FormLabel>
                                        <Select
                                            value={reportFilter}
                                            onChange={(e) => setReportFilter(e.target.value)}
                                            size="sm"
                                            w="full"
                                        >
                                            <option value="all">All Reports</option>
                                            {reportFilterData.map(report => (
                                                <option key={report.reportCode} value={report.reportCode}>
                                                    {report.reportCode} - {report.reportDescription}
                                                </option>
                                            ))}
                                        </Select>
                                    </Box>

                                    <Box>
                                        <FormLabel fontSize="sm" color={textColor} mb={1}>Client</FormLabel>
                                        <Select
                                            value={clientFilter}
                                            onChange={(e) => setClientFilter(e.target.value)}
                                            size="sm"
                                            w="full"
                                        >
                                            <option value="all">All Clients</option>
                                            {clients.map(client => (
                                                <option key={client.clientId} value={client.clientId}>
                                                    {client.clientId} - {client.clientName}
                                                </option>
                                            ))}
                                        </Select>
                                    </Box>

                                    <Box>
                                        <FormLabel fontSize="sm" color={textColor} mb={1}>Status</FormLabel>
                                        <Select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            size="sm"
                                            w="full"
                                        >
                                            <option value="all">All Status</option>
                                            <option value="active">Active</option>
                                            <option value="disabled">Disabled</option>
                                        </Select>
                                    </Box>

                                    <Box>
                                        <FormLabel fontSize="sm" color={textColor} mb={1}>Verification</FormLabel>
                                        <Select
                                            value={verificationFilter}
                                            onChange={(e) => setVerificationFilter(e.target.value)}
                                            size="sm"
                                            w="full"
                                        >
                                            <option value="all">All Verification</option>
                                            <option value="verified">Verified</option>
                                            <option value="unverified">Unverified</option>
                                            <option value="error">Verification Error</option>
                                        </Select>
                                    </Box>

                                    <Box>
                                        <FormLabel fontSize="sm" color={textColor} mb={1}>Cron Trigger</FormLabel>
                                        <Select
                                            value={cronTriggerFilter}
                                            onChange={(e) => setCronTriggerFilter(e.target.value)}
                                            size="sm"
                                            w="full"
                                        >
                                            <option value="all">All Cron Trigger</option>
                                            <option value="enabled">Cron Enabled</option>
                                            <option value="disabled">Cron Disabled</option>
                                        </Select>
                                    </Box>

                                    <Box>
                                        <FormLabel fontSize="sm" color={textColor} mb={1}>Page Size</FormLabel>
                                        <Select
                                            value={pagination.size}
                                            onChange={(e) => handlePageSizeChange(e.target.value)}
                                            size="sm"
                                            w="full"
                                        >
                                            <option value={10}>10 per page</option>
                                            <option value={20}>20 per page</option>
                                            <option value={50}>50 per page</option>
                                            <option value={100}>100 per page</option>
                                        </Select>
                                    </Box>
                                </SimpleGrid>
                            </Box>

                            {/* Active Filters Display with removable tags */}
                            {hasActiveFilters() && (
                                <Box 
                                    p={3} 
                                    bg={activeFilterBg} 
                                    borderRadius="md" 
                                    borderWidth="1px" 
                                    borderColor={activeFilterBorderColor}
                                >
                                    <VStack align="start" spacing={3}>
                                        <HStack spacing={2}>
                                            <Text fontSize="sm" fontWeight="medium" color={textColor}>
                                                Active Filters:
                                            </Text>
                                            <Badge colorScheme="blue" variant="solid" fontSize="xs">
                                                {getActiveFiltersCount()}
                                            </Badge>
                                        </HStack>
                                        <Wrap spacing={2}>
                                            {searchTerm.trim() && (
                                                <WrapItem>
                                                    <Tag size="sm" colorScheme="blue" variant="solid">
                                                        <TagLabel>Search: "{searchTerm.slice(0, 20)}{searchTerm.length > 20 ? '...' : ''}"</TagLabel>
                                                        <TagCloseButton onClick={() => setSearchTerm('')} />
                                                    </Tag>
                                                </WrapItem>
                                            )}
                                            {reportFilter !== 'all' && (
                                                <WrapItem>
                                                    <Tag size="sm" colorScheme="purple" variant="solid">
                                                        <TagLabel>
                                                            Report: {reports.find(r => r.reportCode.toString() === reportFilter)?.reportCode || reportFilter}
                                                        </TagLabel>
                                                        <TagCloseButton onClick={() => setReportFilter('all')} />
                                                    </Tag>
                                                </WrapItem>
                                            )}
                                            {clientFilter !== 'all' && (
                                                <WrapItem>
                                                    <Tag size="sm" colorScheme="green" variant="solid">
                                                        <TagLabel>
                                                            Client: {clients.find(c => c.clientId.toString() === clientFilter)?.clientName || clientFilter}
                                                        </TagLabel>
                                                        <TagCloseButton onClick={() => setClientFilter('all')} />
                                                    </Tag>
                                                </WrapItem>
                                            )}
                                            {statusFilter !== 'all' && (
                                                <WrapItem>
                                                    <Tag size="sm" colorScheme="orange" variant="solid">
                                                        <TagLabel>Status: {statusFilter}</TagLabel>
                                                        <TagCloseButton onClick={() => setStatusFilter('all')} />
                                                    </Tag>
                                                </WrapItem>
                                            )}
                                            {verificationFilter !== 'all' && (
                                                <WrapItem>
                                                    <Tag size="sm" colorScheme="yellow" variant="solid">
                                                        <TagLabel>Verification: {verificationFilter}</TagLabel>
                                                        <TagCloseButton onClick={() => setVerificationFilter('all')} />
                                                    </Tag>
                                                </WrapItem>
                                            )}
                                            {cronTriggerFilter !== 'all' && (
                                                <WrapItem>
                                                    <Tag size="sm" colorScheme="cyan" variant="solid">
                                                        <TagLabel>Cron: {cronTriggerFilter}</TagLabel>
                                                        <TagCloseButton onClick={() => setCronTriggerFilter('all')} />
                                                    </Tag>
                                                </WrapItem>
                                            )}
                                        </Wrap>
                                    </VStack>
                                </Box>
                            )}

                            {/* Bulk Action Buttons for selected mappings */}
                            {selectedMappings.length > 0 && (
                                <Box 
                                    bg={bulkActionBg} 
                                    borderRadius="md" 
                                    p={3}
                                    borderWidth="1px" 
                                    borderColor={bulkActionBorderColor}
                                >
                                    <Flex 
                                        direction={{ base: 'column', sm: 'row' }} 
                                        align={{ base: 'start', sm: 'center' }} 
                                        gap={3}
                                    >
                                        <Text fontSize="sm" fontWeight="medium" color={textColor}>
                                            Bulk Actions:
                                        </Text>
                                        <Button
                                            leftIcon={<MdDelete />}
                                            onClick={handleBulkDelete}
                                            colorScheme="red"
                                            variant="solid"
                                            size="sm"
                                        >
                                            Delete Selected ({selectedMappings.length})
                                        </Button>
                                    </Flex>
                                </Box>
                            )}
                        </VStack>
                    </CardBody>
                </Card>

                {/* Mappings Table with comprehensive mapping display */}
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                        <HStack justify="space-between">
                            <Heading size="md">
                                Mappings ({formatNumber(pagination.total)})
                            </Heading>
                            <HStack spacing={4}>
                                <Badge colorScheme="blue" variant="subtle">
                                    Page {pagination.page} of {pagination.totalPages || 1}
                                </Badge>
                                <Badge colorScheme="green" variant="subtle">
                                    Showing {filteredMappings.length} of {formatNumber(pagination.total)}
                                </Badge>
                            </HStack>
                        </HStack>
                    </CardHeader>
                    <CardBody p={0}>
                        {loading ? (
                            <Flex justify="center" p={8}>
                                <VStack spacing={4}>
                                    <Spinner size="lg" color="brand.500" thickness="3px" />
                                    <Text color={textColor}>Loading mappings...</Text>
                                </VStack>
                            </Flex>
                        ) : (
                            <>
                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th w="50px">
                                                    <Checkbox
                                                        isChecked={selectedMappings.length === filteredMappings.length && filteredMappings.length > 0}
                                                        isIndeterminate={selectedMappings.length > 0 && selectedMappings.length < filteredMappings.length}
                                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                                    />
                                                </Th>
                                                <Th>Client</Th>
                                                <Th>Report</Th>
                                                <Th>Status</Th>
                                                <Th>Verification</Th>
                                                <Th>Custom Cron Scheduler</Th>
                                                <Th>Created</Th>
                                                <Th w="120px">Actions</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {filteredMappings.map((mapping) => {
                                                const mappingId = `${mapping.clientId}_${mapping.reportCode}`;
                                                const statusBadge = getStatusBadgeProps(mapping);
                                                
                                                return (
                                                    <Tr key={mappingId}>
                                                        <Td>
                                                            <Checkbox
                                                                isChecked={selectedMappings.includes(mappingId)}
                                                                onChange={(e) => handleMappingSelection(mappingId, e.target.checked)}
                                                            />
                                                        </Td>
                                                        <Td>
                                                            <VStack align="start" spacing={1}>
                                                                <Text fontWeight="medium" fontSize="sm">
                                                                    {mapping.clientName}
                                                                </Text>
                                                                <Badge variant="outline" colorScheme="gray" fontSize="xs">
                                                                    ID: {mapping.clientId}
                                                                </Badge>
                                                            </VStack>
                                                        </Td>
                                                        <Td>
                                                            <VStack align="start" spacing={1}>
                                                                <Badge colorScheme="blue" variant="subtle">
                                                                    {mapping.reportCode}
                                                                </Badge>
                                                                <Text fontSize="xs" color={textColor} noOfLines={2}>
                                                                    {mapping.reportDescription}
                                                                </Text>
                                                            </VStack>
                                                        </Td>
                                                        <Td>
                                                            <Badge 
                                                                colorScheme={statusBadge.colorScheme} 
                                                                variant="subtle"
                                                            >
                                                                {statusBadge.label}
                                                            </Badge>
                                                        </Td>
                                                        <Td>
                                                            <VStack align="start" spacing={1}>
                                                                <HStack>
                                                                    <Badge
                                                                        colorScheme={mapping.isVerified ? 'green' : 'yellow'}
                                                                        variant="subtle"
                                                                        size="sm"
                                                                    >
                                                                        {mapping.isVerified ? 'Verified' : 'Unverified'}
                                                                    </Badge>
                                                                    {mapping.isVerificationError && (
                                                                        <Tooltip label="Verification Error">
                                                                            <Badge colorScheme="red" size="sm">
                                                                                <MdError />
                                                                            </Badge>
                                                                        </Tooltip>
                                                                    )}
                                                                </HStack>
                                                                <Button
                                                                    size="xs"
                                                                    leftIcon={<MdVerifiedUser />}
                                                                    onClick={() => openVerificationModal(mapping)}
                                                                    variant="ghost"
                                                                >
                                                                    Update
                                                                </Button>
                                                            </VStack>
                                                        </Td>
                                                        <Td>
                                                            <VStack align="start" spacing={1}>
                                                                <Switch
                                                                    size="sm"
                                                                    isChecked={mapping.isCronTriggered}
                                                                    onChange={() => handleToggleCronTrigger(mapping)}
                                                                    colorScheme="blue"
                                                                />
                                                                <Text fontSize="xs" color={textColor}>
                                                                    {mapping.isCronTriggered ? 'Enabled' : 'Disabled'}
                                                                </Text>
                                                            </VStack>
                                                        </Td>
                                                        <Td>
                                                            <VStack align="start" spacing={1}>
                                                                <Text fontSize="sm" color={textColor}>
                                                                    {formatDateTime(mapping.createdAtUtc)}
                                                                </Text>
                                                                <Text fontSize="xs" color={textColor}>
                                                                    by {mapping.createdBy}
                                                                </Text>
                                                            </VStack>
                                                        </Td>
                                                        <Td>
                                                            <Menu>
                                                                <MenuButton
                                                                    as={IconButton}
                                                                    icon={<MdMoreVert />}
                                                                    variant="ghost"
                                                                    size="sm"
                                                                />
                                                                <MenuList>
                                                                    <MenuItem
                                                                        icon={<MdEdit />}
                                                                        onClick={() => openEditModal(mapping)}
                                                                    >
                                                                        Edit
                                                                    </MenuItem>
                                                                    <MenuItem
                                                                        icon={<MdVerifiedUser />}
                                                                        onClick={() => openVerificationModal(mapping)}
                                                                    >
                                                                        Verification
                                                                    </MenuItem>
                                                                    <MenuItem
                                                                        icon={<MdDelete />}
                                                                        onClick={() => openDeleteModal(mapping)}
                                                                        color="red.500"
                                                                    >
                                                                        Delete
                                                                    </MenuItem>
                                                                </MenuList>
                                                            </Menu>
                                                        </Td>
                                                    </Tr>
                                                );
                                            })}
                                        </Tbody>
                                    </Table>
                                </TableContainer>

                                {/* Enhanced Pagination Controls */}
                                {pagination.totalPages > 1 && (
                                    <Flex justify="space-between" align="center" p={4} borderTop="1px" borderTopColor={borderColor}>
                                        <Text fontSize="sm" color={textColor}>
                                            Showing {((pagination.page - 1) * pagination.size) + 1} to {Math.min(pagination.page * pagination.size, pagination.total)} of {pagination.total} entries
                                        </Text>
                                        
                                        <HStack spacing={2}>
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(1)}
                                                isDisabled={!pagination.hasPrevious}
                                                variant="outline"
                                            >
                                                First
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(pagination.page - 1)}
                                                isDisabled={!pagination.hasPrevious}
                                                variant="outline"
                                            >
                                                Previous
                                            </Button>
                                            
                                            {/* Page Numbers */}
                                            {(() => {
                                                const pages = [];
                                                const maxVisible = 5;
                                                let startPage = Math.max(1, pagination.page - 2);
                                                let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);
                                                
                                                if (endPage - startPage < maxVisible - 1) {
                                                    startPage = Math.max(1, endPage - maxVisible + 1);
                                                }
                                                
                                                for (let i = startPage; i <= endPage; i++) {
                                                    pages.push(
                                                        <Button
                                                            key={i}
                                                            size="sm"
                                                            variant={i === pagination.page ? "solid" : "outline"}
                                                            colorScheme={i === pagination.page ? "brand" : "gray"}
                                                            onClick={() => handlePageChange(i)}
                                                        >
                                                            {i}
                                                        </Button>
                                                    );
                                                }
                                                return pages;
                                            })()}
                                            
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(pagination.page + 1)}
                                                isDisabled={!pagination.hasNext}
                                                variant="outline"
                                            >
                                                Next
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(pagination.totalPages)}
                                                isDisabled={!pagination.hasNext}
                                                variant="outline"
                                            >
                                                Last
                                            </Button>
                                        </HStack>
                                    </Flex>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>

                {/* Create Mapping Modal with comprehensive form */}
                <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="lg">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Create Report Mapping</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <VStack spacing={4}>
                                <FormControl isRequired>
                                    <FormLabel>Module</FormLabel>
                                    <ReactSelect
                                        options={getReportModules()}
                                        value={getReportModules().find(opt => opt.value === formData.reportModule)}
                                        onChange={ handleReportModuleChange}
                                        placeholder="Select a Module..."
                                        styles={selectStyles}
                                        isSearchable
                                    />
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel>Report</FormLabel>
                                    <ReactSelect
                                        key={formData.reportModule} 
                                        options={getReportOptions()}
                                        value={getReportOptions().find(opt => opt.value === formData.reportCode)}
                                        onChange={handleReportSelectionChange}
                                        placeholder="Select a report..."
                                        styles={selectStyles}
                                        isSearchable
                                    />
                                </FormControl>
                                
                                <FormControl isRequired>
                                    <FormLabel>
                                        Clients
                                        {formData.reportCode && (
                                            <Text as="span" fontSize="sm" color="gray.500" ml={2}>
                                                ({availableClients.length} available for mapping)
                                            </Text>
                                        )}
                                    </FormLabel>
                                    <ReactSelect
                                        options={[
                                            { value: 'all', label: `All Available Clients (${availableClients.length})` },
                                            ...getAvailableClientOptions()
                                        ]}
                                        value={getSelectedClientOptions()}
                                        onChange={handleClientSelectionChange}
                                        placeholder={
                                            loadingClients ? "Loading available clients..." : 
                                            !formData.reportCode ? "Select a report first..." :
                                            availableClients.length === 0 ? "No unmapped clients available" :
                                            "Select clients..."
                                        }
                                        styles={selectStyles}
                                        isMulti
                                        isSearchable
                                        closeMenuOnSelect={false}
                                        isDisabled={!formData.reportCode || loadingClients || availableClients.length === 0}
                                        isLoading={loadingClients}
                                    />
                                    {formData.reportCode && (
                                        <Box mt={2}>
                                            {availableClients.length === 0 ? (
                                                <Text fontSize="sm" color="orange.500">
                                                    ⚠️ All clients are already mapped to this report
                                                </Text>
                                            ) : formData.clientIds.includes('all') ? (
                                                <Text fontSize="sm" color="blue.500">
                                                    ✓ All {availableClients.length} available clients will be mapped to this report
                                                </Text>
                                            ) : formData.clientIds.length > 0 ? (
                                                <Text fontSize="sm" color="green.500">
                                                    ✓ {formData.clientIds.length} client(s) selected for mapping
                                                </Text>
                                            ) : (
                                                <Text fontSize="sm" color="gray.500">
                                                    💡 Select clients to map to this report (excludes already mapped clients)
                                                </Text>
                                            )}
                                        </Box>
                                    )}
                                </FormControl>
                                
                                <FormControl>
                                    <FormLabel>Status</FormLabel>
                                    <HStack>
                                        <Switch
                                            isChecked={formData.isEnabled}
                                            onChange={(e) => setFormData(prev => ({ 
                                                ...prev, 
                                                isEnabled: e.target.checked 
                                            }))}
                                        />
                                        <Text fontSize="sm">
                                            {formData.isEnabled ? 'Enabled' : 'Disabled'}
                                        </Text>
                                    </HStack>
                                </FormControl>
                            </VStack>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onCreateClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="brand" onClick={handleCreateMapping}>
                                Create Mapping
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Edit Mapping Modal for updating existing mappings */}
                <Modal isOpen={isEditOpen} onClose={onEditClose} size="lg">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Edit Mapping</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <VStack spacing={4}>
                                <FormControl>
                                    <FormLabel>Report Code</FormLabel>
                                    <Input
                                        value={formData.reportCode || ''}
                                        isReadOnly
                                        bg={readOnlyInputBg}
                                    />
                                </FormControl>
                                
                                <FormControl>
                                    <FormLabel>Client</FormLabel>
                                    <Input
                                        value={currentMapping ? `${currentMapping.clientId} - ${currentMapping.clientName}` : ''}
                                        isReadOnly
                                        bg={readOnlyInputBg}
                                    />
                                </FormControl>
                                
                                <FormControl>
                                    <FormLabel>Status</FormLabel>
                                    <HStack>
                                        <Switch
                                            isChecked={formData.isEnabled}
                                            onChange={(e) => setFormData(prev => ({ 
                                                ...prev, 
                                                isEnabled: e.target.checked 
                                            }))}
                                        />
                                        <Text fontSize="sm">
                                            {formData.isEnabled ? 'Enabled' : 'Disabled'}
                                        </Text>
                                    </HStack>
                                </FormControl>
                            </VStack>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onEditClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="brand" onClick={handleUpdateMapping}>
                                Update Mapping
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Verification Modal for DCN validation status management */}
                <Modal isOpen={isVerificationOpen} onClose={onVerificationClose} size="md">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Update Verification Status</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <VStack spacing={4}>
                                <Alert status="info" borderRadius="md">
                                    <AlertIcon />
                                    Update verification status for this mapping based on DCN validation results.
                                </Alert>
                                
                                <FormControl>
                                    <FormLabel>Verification Status</FormLabel>
                                    <HStack>
                                        <Switch
                                            isChecked={verificationData.isVerified}
                                            onChange={(e) => setVerificationData(prev => ({ 
                                                ...prev, 
                                                isVerified: e.target.checked 
                                            }))}
                                        />
                                        <Text fontSize="sm">
                                            {verificationData.isVerified ? 'Verified' : 'Unverified'}
                                        </Text>
                                    </HStack>
                                </FormControl>
                                
                                <FormControl>
                                    <FormLabel>Has Error</FormLabel>
                                    <HStack>
                                        <Switch
                                            isChecked={verificationData.hasError}
                                            onChange={(e) => setVerificationData(prev => ({ 
                                                ...prev, 
                                                hasError: e.target.checked 
                                            }))}
                                        />
                                        <Text fontSize="sm">
                                            {verificationData.hasError ? 'Error' : 'No Error'}
                                        </Text>
                                    </HStack>
                                </FormControl>
                            </VStack>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onVerificationClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="brand" onClick={handleUpdateVerification}>
                                Update Verification
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Delete Confirmation Modal with warning */}
                <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Confirm Deletion</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <Alert status="warning" borderRadius="md">
                                <AlertIcon />
                                Are you sure you want to delete the mapping between "{currentMapping?.reportCode}" and "{currentMapping?.clientName}"? This action cannot be undone.
                            </Alert>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={handleDeleteMapping}>
                                Delete Mapping
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </VStack>
        </Box>
    );
}

export default MapReport;